import React from 'react';
import { TextBlock } from '../../../core/ast/document.types';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Highlighter,
} from 'lucide-react';

interface TextBlockEditorProps {
  block: TextBlock;
  onUpdate: (updates: Partial<TextBlock['data']>) => void;
}

export const TextBlockEditor: React.FC<TextBlockEditorProps> = ({ block, onUpdate }) => {
  const { text, formatting = {}, level = 'paragraph' } = block.data;

  const toggleFormat = (key: 'bold' | 'italic' | 'underline' | 'strikethrough') => {
    onUpdate({
      formatting: {
        ...formatting,
        [key]: !formatting[key],
      },
    });
  };

  const setAlign = (alignment: 'left' | 'center' | 'right' | 'justify') => {
    onUpdate({
      formatting: {
        ...formatting,
        alignment,
      },
    });
  };

  const insertPrefix = (prefix: string) => {
    const lines = text.split('\n');
    const updated = lines.map((l) => (l.startsWith(prefix) ? l : `${prefix}${l}`)).join('\n');
    onUpdate({ text: updated });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {/* Inline Rich Text Formatting Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid var(--border-subtle)',
          paddingBottom: 4,
          flexWrap: 'wrap',
        }}
      >
        {/* Heading Level Selector */}
        <select
          className="form-select text-xs"
          style={{ width: '110px', height: '26px', padding: '2px 4px', fontSize: '11px' }}
          value={level}
          onChange={(e) => onUpdate({ level: e.target.value as any })}
        >
          <option value="paragraph">Paragraph</option>
          <option value="heading1">H1 Heading</option>
          <option value="heading2">H2 Subheading</option>
          <option value="heading3">H3 Minor Heading</option>
        </select>

        <span style={{ color: 'var(--border-default)', margin: '0 2px' }}>|</span>

        {/* Font Weight and Styles */}
        <button
          type="button"
          className={`icon-btn ${formatting.bold ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() => toggleFormat('bold')}
          title="Bold (Ctrl+B)"
        >
          <Bold size={12} />
        </button>
        <button
          type="button"
          className={`icon-btn ${formatting.italic ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() => toggleFormat('italic')}
          title="Italic (Ctrl+I)"
        >
          <Italic size={12} />
        </button>
        <button
          type="button"
          className={`icon-btn ${formatting.underline ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() => toggleFormat('underline')}
          title="Underline (Ctrl+U)"
        >
          <Underline size={12} />
        </button>
        <button
          type="button"
          className={`icon-btn ${formatting.strikethrough ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() => toggleFormat('strikethrough')}
          title="Strikethrough"
        >
          <Strikethrough size={12} />
        </button>

        <span style={{ color: 'var(--border-default)', margin: '0 2px' }}>|</span>

        {/* Text Alignment */}
        <button
          type="button"
          className={`icon-btn ${formatting.alignment === 'left' || !formatting.alignment ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() => setAlign('left')}
          title="Align Left"
        >
          <AlignLeft size={12} />
        </button>
        <button
          type="button"
          className={`icon-btn ${formatting.alignment === 'center' ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() => setAlign('center')}
          title="Align Center"
        >
          <AlignCenter size={12} />
        </button>
        <button
          type="button"
          className={`icon-btn ${formatting.alignment === 'right' ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() => setAlign('right')}
          title="Align Right"
        >
          <AlignRight size={12} />
        </button>
        <button
          type="button"
          className={`icon-btn ${formatting.alignment === 'justify' ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() => setAlign('justify')}
          title="Justify"
        >
          <AlignJustify size={12} />
        </button>

        <span style={{ color: 'var(--border-default)', margin: '0 2px' }}>|</span>

        {/* List Helpers */}
        <button
          type="button"
          className="icon-btn"
          style={{ width: 24, height: 24 }}
          onClick={() => insertPrefix('• ')}
          title="Add Bullet List Prefix"
        >
          <List size={12} />
        </button>
        <button
          type="button"
          className="icon-btn"
          style={{ width: 24, height: 24 }}
          onClick={() => insertPrefix('1. ')}
          title="Add Numbered List Prefix"
        >
          <ListOrdered size={12} />
        </button>

        {/* Highlight Color */}
        <button
          type="button"
          className={`icon-btn ${formatting.backgroundColor ? 'active' : ''}`}
          style={{ width: 24, height: 24 }}
          onClick={() =>
            onUpdate({
              formatting: {
                ...formatting,
                backgroundColor: formatting.backgroundColor ? undefined : '#fef3c7',
              },
            })
          }
          title="Toggle Highlight"
        >
          <Highlighter size={12} color={formatting.backgroundColor ? '#d97706' : 'currentColor'} />
        </button>
      </div>

      {/* Main Text Content Area */}
      <textarea
        className="form-textarea"
        rows={level === 'heading1' ? 1 : level === 'heading2' ? 2 : 3}
        value={text}
        onChange={(e) => onUpdate({ text: e.target.value })}
        placeholder={
          level === 'heading1'
            ? 'Heading 1 Title...'
            : level === 'heading2'
            ? 'Heading 2 Subtitle...'
            : 'Enter paragraph text, laboratory procedure steps, theoretical models, or discussion...'
        }
        style={{
          fontWeight: formatting.bold || level === 'heading1' || level === 'heading2' ? 'bold' : 'normal',
          fontStyle: formatting.italic ? 'italic' : 'normal',
          textDecoration: formatting.underline
            ? 'underline'
            : formatting.strikethrough
            ? 'line-through'
            : 'none',
          textAlign: formatting.alignment || 'left',
          fontSize:
            level === 'heading1'
              ? 'var(--font-size-md)'
              : level === 'heading2'
              ? 'var(--font-size-sm)'
              : 'var(--font-size-xs)',
          backgroundColor: formatting.backgroundColor || undefined,
        }}
      />
    </div>
  );
};
